import * as CertGenConstants from "./CertGenConstants";

// Object.assign is not yet fully supported in all browsers, so we fallback to a polyfill
const assign = Object.assign || require("object.assign");

/**
 * initial state which is managed by CertGenReducer
 */
const initialState = {
  certGenReqStatus: CertGenConstants.CERT_GEN_REQ_STATUS_SUCCESS, // SUCCESS -> REQUESTED -> FAILED | SUCCESS
  certGenReqErrorMessage: "",
  certGenReqPayload: null,
  fetchLogStatus: CertGenConstants.FETCH_LOG_STATUS_SUCCESS, // SUCCESS -> FETCHING -> FAILED | SUCCESS
  fetchLogErrorMessage: "",
  certGenLog: [],
  downloadCertStatus: CertGenConstants.DOWNLOAD_CERT_STATUS_SUCCESS, // SUCCESS -> DOWNLOADING -> FAILED | SUCCESS
  downloadErrorMessage: "",
  downloadCertPayload: null,
};

const getDownloadErrorMessage = (errorPayload) => {
  const statusCode =
    errorPayload?.statusCode ||
    errorPayload?.status ||
    errorPayload?.httpStatus ||
    errorPayload?.responseJSON?.statusCode ||
    errorPayload?.responseJSON?.status;

  if (
    (typeof statusCode === "string" && (statusCode.includes("NOT_FOUND") || statusCode.includes("404"))) ||
    statusCode === 404
  ) {
    return CertGenConstants.CERT_NOT_FOUND_ERROR_MESSAGE;
  }

  if (
    (typeof statusCode === "string" && (statusCode.includes("INTERNAL_SERVER_ERROR") || statusCode.includes("500"))) ||
    statusCode === 500
  ) {
    return CertGenConstants.GENERIC_ERROR_MESSAGE;
  }

  return (
    errorPayload?.debugMessage ||
    errorPayload?.responseJSON?.debugMessage ||
    CertGenConstants.GENERIC_ERROR_MESSAGE
  );
};

/**'
 * redux reducer method which takes the current state and the action as input parameters and returns with the new state
 */
export default function CertGenReducer(state = initialState, action) {
  switch (action.type) {
    /**
     * updation of state according to the fetch user certificate requests log actions dispatched
     */
    case CertGenConstants.CERT_GEN_REQUESTS_FETCH_REQUEST: {
      const newState = {
        ...state,
        fetchLogStatus: CertGenConstants.FETCH_LOG_STATUS_FETCHING,
        fetchLogErrorMessage: "",
        certGenLog: [],
      };
      return assign({}, state, newState);
    }
    case CertGenConstants.CERT_GEN_REQUESTS_FETCH_SUCCESS: {
      const newState = {
        ...state,
        fetchLogStatus: CertGenConstants.FETCH_LOG_STATUS_SUCCESS,
        fetchLogErrorMessage: "",
        certGenLog: [...action.payload],
      };
      return assign({}, state, newState);
    }
    case CertGenConstants.CERT_GEN_REQUESTS_FETCH_ERROR: {
      const newState = {
        ...state,
        fetchLogStatus: CertGenConstants.FETCH_LOG_STATUS_FAILED,
        fetchLogErrorMessage:
          action.payload?.debugMessage ||
          CertGenConstants.GENERIC_ERROR_MESSAGE,
        certGenLog: [],
      };
      return assign({}, state, newState);
    }
    /**
     * updation of state according to the create new certificate request actions dispatched
     */
    case CertGenConstants.REQUEST_CERT_GEN: {
      const newState = {
        ...state,
        certGenReqStatus: CertGenConstants.CERT_GEN_REQ_STATUS_REQUESTED,
        certGenReqErrorMessage: "",
        certGenReqPayload: action.payload,
      };
      return assign({}, state, newState);
    }
    case CertGenConstants.REQUEST_CERT_GEN_SUCCESS: {
      const certGenLogEntryIndex = state.certGenLog.findIndex(
        (el) => el.instanceId === action.payload.instanceId
      );
      let newCertGenLog = state.certGenLog;
      if (certGenLogEntryIndex !== -1) {
        newCertGenLog.splice(certGenLogEntryIndex, 1);
      }

      const newState = {
        ...state,
        certGenReqStatus: CertGenConstants.CERT_GEN_REQ_STATUS_SUCCESS,
        certGenReqErrorMessage: "",
        certGenLog: [action.payload, ...newCertGenLog],
      };
      return assign({}, state, newState);
    }
    case CertGenConstants.REQUEST_CERT_GEN_ERROR: {
      const newState = {
        ...state,
        certGenReqStatus: CertGenConstants.CERT_GEN_REQ_STATUS_FAILED,
        certGenReqErrorMessage:
          action.payload?.debugMessage ||
          CertGenConstants.GENERIC_ERROR_MESSAGE,
      };
      return assign({}, state, newState);
    }
    /**
     * updation of state when the close button is clicked on the certificate request error popup
     */
    case CertGenConstants.CLOSE_REQUEST_CERT_GEN_ERROR_POPUP: {
      const newState = {
        ...state,
        certGenReqStatus: CertGenConstants.CERT_GEN_REQ_STATUS_SUCCESS,
        certGenReqErrorMessage: "",
        certGenReqPayload: null,
      };
      return assign({}, state, newState);
    }
    /**
     * updation of state according to the download user certificate actions dispatched
     */
    case CertGenConstants.DOWNLOAD_CERT_REQUEST: {
      const newState = {
        ...state,
        downloadCertStatus: CertGenConstants.DOWNLOAD_CERT_STATUS_DOWNLOADING,
        downloadErrorMessage: "",
        downloadCertPayload: action.payload,
      };
      return assign({}, state, newState);
    }
    case CertGenConstants.DOWNLOAD_CERT_SUCCESS: {
      const newState = {
        ...state,
        downloadCertStatus: CertGenConstants.DOWNLOAD_CERT_STATUS_SUCCESS,
        downloadErrorMessage: "",
      };
      return assign({}, state, newState);
    }
    case CertGenConstants.DOWNLOAD_CERT_ERROR: {
      const newState = {
        ...state,
        downloadCertStatus: CertGenConstants.DOWNLOAD_CERT_STATUS_FAILED,
        downloadErrorMessage: getDownloadErrorMessage(action.payload),
      };
      return assign({}, state, newState);
    }
    /**
     * updation of state when the close button is clicked on the download certificate error popup
     */
    case CertGenConstants.CLOSE_DOWNLOAD_CERT_ERROR_POPUP: {
      const newState = {
        ...state,
        downloadCertStatus: CertGenConstants.DOWNLOAD_CERT_STATUS_SUCCESS,
        downloadErrorMessage: "",
        downloadCertPayload: null,
      };
      return assign({}, state, newState);
    }
    /**
     * updation of state according when a particular user certificate request log entry needs to be updated
     */
    case CertGenConstants.UPDATE_CERT_GEN_LOG_ENTRY_STATUS: {
      const logEntryIndex = state.certGenLog.findIndex(
        (logEntry) =>
          action.payload.requestId === logEntry.topicListener?.subscription
      );
      if (logEntryIndex === -1) {
        return state;
      }

      const certGenLogEntry = {
        ...state.certGenLog[logEntryIndex],
      };
      certGenLogEntry.status = action.payload.status;
      if (action.payload.status === CertGenConstants.CertStatus.CREATED.key) {
        certGenLogEntry.expiryDate = action.payload.expiryDate;
      }
      certGenLogEntry.topicListener = null;

      const newState = {
        ...state,
        certGenLog: [
          ...state.certGenLog.slice(0, logEntryIndex),
          certGenLogEntry,
          ...state.certGenLog.slice(logEntryIndex + 1),
        ],
      };

      return assign({}, state, newState);
    }
    default:
      return state;
  }
}
